// Tiny typed helpers over localStorage that encode *scope* in the key.
// Every key in the app should be written through these so that nobody can
// accidentally ship a cross-org (or cross-user) leak again.
//
// Convention:
//   fiduly:o:<orgId>:<key>   → scoped to an organization
//   fiduly:u:<userId>:<key>  → scoped to a user
// Device-level keys (`fiduly-server`, `pwa-install-dismissed`) are kept under
// their historical flat names through `deviceStorage` — they survive logouts
// by design.

const ORG_PREFIX = 'fiduly:o:'
const USER_PREFIX = 'fiduly:u:'

/**
 * Single source of truth for every localStorage key used by the app.
 * Add new keys here instead of hardcoding strings at the call site.
 */
export const STORAGE_KEYS = {
  /** Unscoped device-level keys. Persist across logouts. */
  device: {
    /** Dev-only selector between local/staging/production API. */
    server: 'fiduly-server',
    /** Timestamp at which the PWA install prompt was dismissed. */
    installDismissed: 'pwa-install-dismissed',
  },
  /** Org-scoped. Access via `orgStorage.get(orgId, STORAGE_KEYS.org.xxx)`. */
  org: {
    lastBank: 'last-bank',
    lastCategory: 'last-category',
  },
  /** User-scoped. Access via `userStorage.get(userId, STORAGE_KEYS.user.xxx)`. */
  user: {
    /* Placeholder so the key union is a string (not `never`). Remove once
     * at least one user-scoped key exists. */
    _reserved: '_reserved',
  },
  /**
   * Non-scoped keys that survive a logout by design and are read at module
   * load time (before any session is available). Keep the list short —
   * anything user- or org-specific must move to the scoped stores above.
   */
  crossSession: {
    /** Last org the current device was viewing. */
    lastOrg: 'fiduly-last-org',
  },
} as const

// Narrow key types — passing any other string literal fails at compile time.
type OrgStorageKey = (typeof STORAGE_KEYS.org)[keyof typeof STORAGE_KEYS.org]
type UserStorageKey = (typeof STORAGE_KEYS.user)[keyof typeof STORAGE_KEYS.user]
type DeviceStorageKey =
  | (typeof STORAGE_KEYS.device)[keyof typeof STORAGE_KEYS.device]
  | (typeof STORAGE_KEYS.crossSession)[keyof typeof STORAGE_KEYS.crossSession]

function orgKey(orgId: string, key: string) {
  return `${ORG_PREFIX}${orgId}:${key}`
}

function userKey(userId: string, key: string) {
  return `${USER_PREFIX}${userId}:${key}`
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* quota exceeded, private mode, etc. — persistence is best-effort */
  }
}

function safeRemove(key: string) {
  try {
    localStorage.removeItem(key)
  } catch {
    /* noop */
  }
}

/**
 * Direct access to localStorage for keys that are NOT scoped by org or user.
 * Reserved for truly device-level concerns (dev toggles, install preferences).
 */
export const deviceStorage = {
  get(key: DeviceStorageKey): string | null {
    return safeGet(key)
  },
  set(key: DeviceStorageKey, value: string) {
    safeSet(key, value)
  },
  remove(key: DeviceStorageKey) {
    safeRemove(key)
  },
}

export const orgStorage = {
  get(orgId: string, key: OrgStorageKey): string | null {
    if (!orgId) return null
    return safeGet(orgKey(orgId, key))
  },
  set(orgId: string, key: OrgStorageKey, value: string) {
    if (!orgId) return
    safeSet(orgKey(orgId, key), value)
  },
  remove(orgId: string, key: OrgStorageKey) {
    if (!orgId) return
    safeRemove(orgKey(orgId, key))
  },
  getJSON<T>(orgId: string, key: OrgStorageKey): T | null {
    const raw = this.get(orgId, key)
    if (!raw) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  },
  setJSON<T>(orgId: string, key: OrgStorageKey, value: T) {
    try {
      this.set(orgId, key, JSON.stringify(value))
    } catch {
      /* circular structures etc. */
    }
  },
}

export const userStorage = {
  get(userId: string, key: UserStorageKey): string | null {
    if (!userId) return null
    return safeGet(userKey(userId, key))
  },
  set(userId: string, key: UserStorageKey, value: string) {
    if (!userId) return
    safeSet(userKey(userId, key), value)
  },
  remove(userId: string, key: UserStorageKey) {
    if (!userId) return
    safeRemove(userKey(userId, key))
  },
}

/**
 * Drop every user- and org-scoped key, plus the non-scoped last-org pointer.
 * Device-level keys (`fiduly-server`, `pwa-install-dismissed`) survive so that
 * developer toggles and install-prompt preferences persist across logouts.
 * Call this on logout.
 */
export function clearScopedStorage() {
  try {
    const victims: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k) continue
      if (k.startsWith(ORG_PREFIX) || k.startsWith(USER_PREFIX)) victims.push(k)
    }
    victims.forEach(safeRemove)
    safeRemove(STORAGE_KEYS.crossSession.lastOrg)
  } catch {
    /* noop */
  }
}

/**
 * One-shot cleanup for keys written by previous code paths. Runs at boot and
 * keeps the localStorage inspector tidy. Safe to call repeatedly.
 */
export function cleanupLegacyKeys() {
  safeRemove('fiduly-last-bank')
  safeRemove('fiduly-last-category')
  try {
    const victims: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k) continue
      // Pre-scoped-helper format: fiduly-last-bank:<orgId>, fiduly-last-category:<orgId>
      if (
        k.startsWith('fiduly-last-bank:') ||
        k.startsWith('fiduly-last-category:')
      ) {
        victims.push(k)
      }
    }
    victims.forEach(safeRemove)
  } catch {
    /* noop */
  }
}
